import { z } from "zod"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"
import type { TypedSchemaInput } from "@/utils/typeUtils"
import { requiredTrimmedString } from "@/validation/sharedStringFieldSchemas"

const createNotificationFormSchema = z
  .object({
    fromDatetime: z.string(),
    untilDatetime: z.string(),
    contents: requiredTrimmedString(
      "Provide content for notification"
    ).max(750, "Must be 750 characters or fewer"),
    sendToTerminal: z.boolean(),
    sendToPortal: z.boolean(),
  })
  .superRefine((params, ctx) => {
    if (
      DateFns.isPast(DateFns.parseISO(params.fromDatetime))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Start date must not be in the past",
        path: ["fromDatetime"],
      })
    }

    if (!params.sendToTerminal && !params.sendToPortal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Either terminal or portal must be selected",
        path: ["sendToTerminal"],
      })
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Either terminal or portal must be selected",
        path: ["sendToPortal"],
      })
    }

    if (
      !DateFns.isValid(
        DateFns.parseISO(params.fromDatetime)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid datetime",
        path: ["fromDatetime"],
      })
    }

    if (
      !DateFns.isValid(
        DateFns.parseISO(params.untilDatetime)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid datetime",
        path: ["untilDatetime"],
      })
    }

    if (
      DateFns.isBefore(
        DateFns.parseISO(params.untilDatetime),
        new Date()
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End cannot be in the past",
        path: ["untilDatetime"],
      })
    }

    if (
      DateFns.isAfter(
        DateFns.parseISO(params.fromDatetime),
        DateFns.parseISO(params.untilDatetime)
      ) ||
      DateFns.isEqual(
        DateFns.parseISO(params.fromDatetime),
        DateFns.parseISO(params.untilDatetime)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End must be after start",
        path: ["untilDatetime"],
      })
    }
  })

export type CreateNotificationFormValues = TypedSchemaInput<
  typeof createNotificationFormSchema
>

const createNotificationInitialValues: CreateNotificationFormValues =
  {
    fromDatetime: DateFns.format(
      DateFns.startOfTomorrow(),
      "yyyy-MM-dd'T'HH:mm"
    ),
    untilDatetime: DateFns.format(
      DateFns.addDays(DateFns.startOfTomorrow(), 1),
      "yyyy-MM-dd'T'HH:mm"
    ),
    contents: "",
    sendToTerminal: false,
    sendToPortal: false,
  }

export const useCreateNotificationForm = () =>
  useForm({
    validationSchema: toTypedSchema(
      createNotificationFormSchema
    ),
    initialValues: createNotificationInitialValues,
  })
